<template>
  <div class="apple-store-page">
    <!-- Apple Nav Header -->
    <AppleNav />

    <main class="store-main">
      <!-- Apple Store Exact Header Section -->
      <section class="store-top-section">
        <div class="store-header-container">
          <!-- Top Split: Title Left / Subheading & Links Right -->
          <div class="store-title-split">
            <div class="store-title-left">
              <h1 class="store-main-title">{{ isEcuador ? 'Tienda' : 'Store' }}</h1>
            </div>

            <div class="store-title-right">
              <h2 class="store-tagline" v-html="isEcuador ? 'La mejor forma de comprar los<br />productos que te encantan.' : 'The best way to buy the<br />products you love.'"></h2>
              
              <div class="store-quick-links">
                <router-link to="/form" class="specialist-link">
                  {{ isEcuador ? 'Habla con un Especialista' : 'Connect with a Specialist' }} <span class="arrow-glyph">↗</span>
                </router-link>
                <router-link to="/store" class="store-locator-link">
                  {{ isEcuador ? 'Buscar una tienda Apple' : 'Find an Apple Store' }} <span class="arrow-glyph">↗</span>
                </router-link>
              </div>
            </div>
          </div>

          <!-- Category Icon Carousel Bar -->
          <div class="category-carousel-wrapper">
            <button 
              v-if="canScrollLeft" 
              class="carousel-nav-btn prev-btn" 
              @click="scrollCategories(-240)"
              aria-label="Previous categories"
            >
              ‹
            </button>

            <div class="category-carousel" ref="categoryScrollRef" @scroll="updateScrollButtons">
              <div 
                v-for="cat in storeCategories" 
                :key="cat.id" 
                class="category-item"
                :class="{ active: activeCategory === cat.id }"
                @click="selectCategory(cat.id)"
              >
                <div class="category-thumb-wrapper">
                  <img :src="cat.image" :alt="cat.name" class="category-thumb-img" />
                </div>
                <span class="category-name">{{ cat.name }}</span>
              </div>
            </div>

            <button 
              v-if="canScrollRight" 
              class="carousel-nav-btn next-btn" 
              @click="scrollCategories(240)"
              aria-label="Next categories"
            >
              ›
            </button>
          </div>
        </div>
      </section>

      <!-- Store Products Category Carousels -->
      <section class="store-products-section">
        <div 
          v-for="section in visibleSections" 
          :key="section.id" 
          :id="'section-' + section.id"
          class="store-category-block"
        >
          <!-- Category Section Header -->
          <div class="category-block-header">
            <h2 class="category-block-title">
              <span class="category-title-bold">{{ section.title }}</span>
              <span class="category-title-sub">{{ section.subheading }}</span>
            </h2>
          </div>

          <!-- Horizontal Carousel Track -->
          <div class="carousel-track-wrapper">
            <button 
              class="track-nav-btn prev-track-btn" 
              @click="scrollTrack(section.id, -400)"
              aria-label="Scroll carousel left"
            >
              ‹
            </button>

            <div 
              class="products-carousel-track" 
              :ref="'track_' + section.id"
            >
              <div 
                v-for="product in section.products" 
                :key="product.id" 
                class="product-card"
                @click="goToForm(product)"
              >
                <!-- Card Top Discount Badge -->
                <span v-if="product.discount" class="card-top-badge">{{ product.discount }}</span>

                <!-- Card Eyebrow -->
                <div v-if="product.eyebrow" class="card-eyebrow">{{ product.eyebrow }}</div>

                <!-- Card Title -->
                <h3 class="card-title" v-html="product.nameHtml || product.name"></h3>

                <!-- Product Image Showcase -->
                <div class="product-img-wrapper">
                  <img 
                    :src="product.image" 
                    :alt="product.name" 
                    class="product-img"
                    :class="[product.imgClass, { 'blend-multiply': product.blendMultiply }]"
                    loading="lazy"
                  />
                </div>

                <!-- Color Variant Dots -->
                <div v-if="product.colors && product.colors.length" class="color-dots-row">
                  <span 
                    v-for="(color, idx) in product.colors" 
                    :key="idx" 
                    class="color-dot"
                    :style="{ backgroundColor: color.hex }"
                    :title="color.name"
                  ></span>
                </div>

                <!-- Card Footer: Pricing on Left, Buy Pill on Right -->
                <div class="card-footer">
                  <div class="card-pricing">
                    <div class="price-row">
                      <span class="price-current">{{ product.price || product.pricePrimary }}</span>
                      <span v-if="product.originalPrice" class="price-original">{{ product.originalPrice }}</span>
                    </div>
                    <p v-if="product.priceSecondary" class="price-secondary">{{ product.priceSecondary }}</p>
                  </div>
                  <button 
                    type="button" 
                    class="card-buy-btn"
                    @click.stop="goToForm(product)"
                  >
                    {{ product.buttonText || 'Buy' }}
                  </button>
                </div>
              </div>
            </div>

            <button 
              class="track-nav-btn next-track-btn" 
              @click="scrollTrack(section.id, 400)"
              aria-label="Scroll carousel right"
            >
              ›
            </button>
          </div>
        </div>
      </section>

     
    </main>

    <!-- Apple Directory Footer -->
    <AppleFooter />
  </div>
</template>

<script>
import AppleNav from '@/components/AppleNav.vue';
import AppleFooter from '@/components/AppleFooter.vue';
import { useI18n } from '@/i18n';

export default {
  name: 'StoreView',
  components: {
    AppleNav,
    AppleFooter
  },
  setup() {
    const { t, locale, isEcuador } = useI18n();
    return { t, locale, isEcuador };
  },
  data() {
    return {
      activeCategory: 'all',
      canScrollLeft: false,
      canScrollRight: true
    };
  },
  computed: {
    storeCategories() {
      return [
        {
          id: 'mac',
          name: 'Mac',
          image: require('@/assets/store-cat-mac.png')
        },
        {
          id: 'iphone',
          name: 'iPhone',
          image: require('@/assets/store-cat-iphone.png')
        },
        {
          id: 'ipad',
          name: 'iPad',
          image: require('@/assets/store-cat-ipad.png')
        },
        {
          id: 'vision',
          name: 'Apple Vision Pro',
          image: require('@/assets/store-cat-vision.png')
        },
        {
          id: 'airpods',
          name: 'AirPods',
          image: require('@/assets/store-cat-airpods.png')
        },
        {
          id: 'appletv',
          name: 'Apple TV 4K',
          image: require('@/assets/store-cat-appletv.png')
        },
        {
          id: 'giftcard',
          name: this.isEcuador ? 'Tarjetas de Regalo' : 'Apple Gift Card',
          image: require('@/assets/store-cat-giftcard.png')
        }
      ];
    },
    sections() {
      const ec = this.isEcuador;
      return [
        {
          id: 'iphone',
          title: 'iPhone.',
          subheading: ec ? 'Lo último. Echa un vistazo a las novedades.' : 'The latest. Take a look at what’s new right now.',
          products: [
            {
              id: 'iphone-air',
              category: 'iphone',
              name: 'iPhone Air',
              nameHtml: 'iPhone Air',
              image: require('@/assets/air.png'),
              imgClass: 'img-air-large',
              buttonText: ec ? 'Comprar' : 'Buy',
              blendMultiply: false,
              colors: [
                { name: ec ? 'Azul Cielo' : 'Sky Blue', hex: '#8ecae6' },
                { name: ec ? 'Oro Claro' : 'Light Gold', hex: '#f0e5d8' },
                { name: ec ? 'Blanco Nube' : 'Cloud White', hex: '#f8f9fa' },
                { name: ec ? 'Gris Espacial' : 'Space Gray', hex: '#3a3a3c' }
              ],
              price: '$899',
              originalPrice: '$1,099',
              discount: '-18%'
            },
            {
              id: 'iphone-17',
              category: 'iphone',
              name: 'iPhone 17',
              nameHtml: 'iPhone 17',
              image: require('@/assets/17.png'),
              buttonText: ec ? 'Comprar' : 'Buy',
              blendMultiply: false,
              colors: [
                { name: ec ? 'Oro Claro' : 'Light Gold', hex: '#f0e5d8' },
                { name: ec ? 'Azul Cielo' : 'Sky Blue', hex: '#8ecae6' },
                { name: ec ? 'Blanco Nube' : 'Cloud White', hex: '#f8f9fa' },
                { name: ec ? 'Gris Espacial' : 'Space Gray', hex: '#3a3a3c' }
              ],
              price: '$799',
              originalPrice: '$899',
              discount: '-11%'
            },
            {
              id: 'iphone-17e',
              category: 'iphone',
              name: 'iPhone 17e',
              nameHtml: 'iPhone 17e',
              image: require('@/assets/17e.png'),
              buttonText: ec ? 'Comprar' : 'Buy',
              blendMultiply: false,
              colors: [
                { name: ec ? 'Azul Cielo' : 'Sky Blue', hex: '#8ecae6' },
                { name: ec ? 'Oro Claro' : 'Light Gold', hex: '#f0e5d8' },
                { name: ec ? 'Blanco Nube' : 'Cloud White', hex: '#f8f9fa' },
                { name: ec ? 'Gris Espacial' : 'Space Gray', hex: '#3a3a3c' }
              ],
              price: '$599',
              originalPrice: '$699',
              discount: '-14%'
            },
            {
              id: 'iphone-17-pro',
              category: 'iphone',
              name: 'iPhone 17 Pro & iPhone 17 Pro Max',
              nameHtml: 'iPhone 17 Pro &<br/>iPhone 17 Pro Max',
              image: require('@/assets/iphone17.png'),
              buttonText: ec ? 'Comprar' : 'Buy',
              blendMultiply: false,
              colors: [
                { name: ec ? 'Azul Ultramar' : 'Ultramarine', hex: '#4056a1' },
                { name: ec ? 'Verde Azulado' : 'Teal', hex: '#80ced6' },
                { name: ec ? 'Rosa' : 'Pink', hex: '#f6cadc' },
                { name: ec ? 'Blanco' : 'White', hex: '#f2f2f7' }
              ],
              price: '$999',
              originalPrice: '$1,199',
              discount: '-17%'
            },
            {
              id: 'iphone-16',
              category: 'iphone',
              name: 'iPhone 16',
              nameHtml: 'iPhone 16',
              image: require('@/assets/16.png'),
              buttonText: ec ? 'Comprar' : 'Buy',
              blendMultiply: false,
              colors: [
                { name: ec ? 'Oro Claro' : 'Light Gold', hex: '#f0e5d8' },
                { name: ec ? 'Azul Cielo' : 'Sky Blue', hex: '#8ecae6' },
                { name: ec ? 'Blanco Nube' : 'Cloud White', hex: '#f8f9fa' },
                { name: ec ? 'Gris Espacial' : 'Space Gray', hex: '#3a3a3c' }
              ],
              price: '$649',
              originalPrice: '$799',
              discount: '-19%'
            }
          ]
        },
        {
          id: 'mac',
          title: 'Mac.',
          subheading: ec ? 'Si puedes soñarlo, puedes crearlo con la Mac.' : 'If you can dream it, Mac can do it.',
          products: [
            {
              id: 'macbook-pro-16',
              category: 'mac',
              name: 'MacBook Neon',
              nameHtml: 'MacBook Neon',
              image: require('@/assets/mac neon.jpg'),
              imgClass: 'img-air-large',
              buttonText: ec ? 'Comprar' : 'Buy',
              blendMultiply: false,
              colors: [
                { name: ec ? 'Negro Espacial' : 'Space Black', hex: '#1c1c1e' },
                { name: ec ? 'Plata' : 'Silver', hex: '#e5e5ea' }
              ],
              price: '$1,499',
              originalPrice: '$1,799',
              discount: '-17%'
            },
            {
              id: 'macbook-air-15',
              category: 'mac',
              name: 'MacBook Air',
              nameHtml: 'MacBook Air',
              image: require('@/assets/macbook-air.png'),
              imgClass: 'img-air-large',
              buttonText: ec ? 'Comprar' : 'Buy',
              blendMultiply: false,
              colors: [
                { name: ec ? 'Medianoche' : 'Midnight', hex: '#1e2630' },
                { name: ec ? 'Blanco Estelar' : 'Starlight', hex: '#f0e5d8' },
                { name: ec ? 'Gris Espacial' : 'Space Gray', hex: '#5c5d60' },
                { name: ec ? 'Plata' : 'Silver', hex: '#e5e5ea' }
              ],
              price: '$899',
              originalPrice: '$1,099',
              discount: '-18%'
            },
            {
              id: 'macbook-pro',
              category: 'mac',
              name: 'MacBook Pro',
              nameHtml: 'MacBook Pro',
              image: require('@/assets/macbook-pro.png'),
              imgClass: 'img-air-large',
              buttonText: ec ? 'Comprar' : 'Buy',
              blendMultiply: false,
              colors: [
                { name: ec ? 'Medianoche' : 'Midnight', hex: '#1e2630' },
                { name: ec ? 'Blanco Estelar' : 'Starlight', hex: '#f0e5d8' },
                { name: ec ? 'Gris Espacial' : 'Space Gray', hex: '#5c5d60' },
                { name: ec ? 'Plata' : 'Silver', hex: '#e5e5ea' }
              ],
              price: '$1,499',
              originalPrice: '$1,799',
              discount: '-17%'
            },
            {
              id: 'imac-24',
              category: 'mac',
              name: 'iMac',
              nameHtml: 'iMac',
              image: require('@/assets/imac.png'),
              imgClass: 'img-air-large',
              buttonText: ec ? 'Comprar' : 'Buy',
              blendMultiply: false,
              colors: [
                { name: ec ? 'Verde' : 'Green', hex: '#a3c9a8' },
                { name: ec ? 'Amarillo' : 'Yellow', hex: '#fce38a' },
                { name: ec ? 'Naranja' : 'Orange', hex: '#f38181' },
                { name: ec ? 'Morado' : 'Purple', hex: '#957dad' },
                { name: ec ? 'Azul' : 'Blue', hex: '#90bede' }
              ],
              price: '$1,199',
              originalPrice: '$1,399',
              discount: '-14%'
            }
          ]
        },
        {
          id: 'ipad',
          title: 'iPad.',
          subheading: ec ? 'Toca, dibuja y escribe en un dispositivo mágico.' : 'Touch, draw, and type on one magical device.',
          products: [
            {
              id: 'ipad-pro-13',
              category: 'ipad',
              name: 'iPad Pro',
              nameHtml: 'iPad Pro',
              image: require('@/assets/ipad-pro.png'),
              imgClass: 'img-air-large',
              buttonText: ec ? 'Comprar' : 'Buy',
              blendMultiply: false,
              colors: [
                { name: ec ? 'Negro Espacial' : 'Space Black', hex: '#232326' },
                { name: ec ? 'Plata' : 'Silver', hex: '#e5e5ea' }
              ],
              price: '$899',
              originalPrice: '$1,099',
              discount: '-18%'
            },
            {
              id: 'ipad-air',
              category: 'ipad',
              name: 'iPad Air',
              nameHtml: 'iPad Air',
              image: require('@/assets/store-cat-ipad.png'),
              imgClass: 'img-air-large',
              buttonText: ec ? 'Comprar' : 'Buy',
              blendMultiply: false,
              colors: [
                { name: ec ? 'Gris Espacial' : 'Space Gray', hex: '#3a3a3c' },
                { name: ec ? 'Blanco Estelar' : 'Starlight', hex: '#f0e5d8' },
                { name: ec ? 'Morado' : 'Purple', hex: '#b7b4d1' },
                { name: ec ? 'Azul' : 'Blue', hex: '#96a7b7' }
              ],
              price: '$499',
              originalPrice: '$649',
              discount: '-23%'
            },
            {
              id: 'ipad-mini',
              category: 'ipad',
              name: 'iPad',
              nameHtml: 'iPad',
              image: require('@/assets/ipad.png'),
              imgClass: 'img-air-large',
              buttonText: ec ? 'Comprar' : 'Buy',
              blendMultiply: false,
              colors: [
                { name: ec ? 'Gris Espacial' : 'Space Gray', hex: '#3a3a3c' },
                { name: ec ? 'Blanco Estelar' : 'Starlight', hex: '#f0e5d8' },
                { name: ec ? 'Morado' : 'Purple', hex: '#b7b4d1' },
                { name: ec ? 'Azul' : 'Blue', hex: '#96a7b7' }
              ],
              price: '$329',
              originalPrice: '$449',
              discount: '-27%'
            }
          ]
        },
        {
          id: 'vision',
          title: ec ? 'Vision y Audio.' : 'Vision & Audio.',
          subheading: ec ? 'Inmersión espacial y audio de última generación.' : 'Next-level spatial audio and computational immersion.',
          products: [
            {
              id: 'apple-vision-pro',
              category: 'vision',
              name: 'Apple Vision Pro',
              nameHtml: 'Apple Vision Pro',
              image: require('@/assets/apple-vision-pro.png'),
              imgClass: 'img-air-large',
              buttonText: ec ? 'Comprar' : 'Buy',
              blendMultiply: false,
              colors: [
                { name: ec ? 'Banda Solo Knit' : 'Solo Knit Band', hex: '#48484a' },
                { name: ec ? 'Banda Dual Loop' : 'Dual Loop Band', hex: '#8e8e93' }
              ],
              price: '$2,999',
              originalPrice: '$3,499',
              discount: '-14%'
            },
            {
              id: 'airpods-pro-3',
              category: 'airpods',
              name: 'AirPods 5',
              nameHtml: 'AirPods 5',
              image: require('@/assets/airpod-pro.png'),
              imgClass: 'img-air-large',
              buttonText: ec ? 'Comprar' : 'Buy',
              blendMultiply: false,
              colors: [
                { name: ec ? 'Blanco' : 'White', hex: '#ffffff' }
              ],
              price: '$149',
              originalPrice: '$199',
              discount: '-25%'
            }
          ]
        },
        {
          id: 'giftcard',
          title: ec ? 'Tarjetas de Regalo Apple.' : 'Apple Gift Cards.',
          subheading: ec ? 'Para todo y para todos.' : 'For everything and everyone Apple.',
          products: [
            {
              id: 'apple-gift-card',
              category: 'giftcard',
              name: 'Apple Card',
              nameHtml: 'Apple Card',
              image: require('@/assets/giftcard.png'),
              buttonText: ec ? 'Comprar / Canjear' : 'Buy',
              blendMultiply: false,
              colors: [
                { name: ec ? 'Rojo Festivo' : 'Red Festive', hex: '#ff3b30' },
                { name: ec ? 'Oro Apple' : 'Apple Gold', hex: '#d4af37' },
                { name: ec ? 'Plata Clásica' : 'Silver Classic', hex: '#e5e5ea' }
              ],
              price: '$100 – $1,000'
            }
          ]
        }
      ];
    },
    visibleSections() {
      if (this.activeCategory === 'all') return this.sections;
      // If a category was chosen, filter to matching section (or related)
      const found = this.sections.filter(s => {
        if (s.id === this.activeCategory) return true;
        if (this.activeCategory === 'airpods' && s.id === 'vision') return true;
        return false;
      });
      return found.length ? found : this.sections;
    }
  },
  watch: {
    isEcuador: {
      immediate: true,
      handler(val) {
        document.title = val ? 'Apple Reseller Store - Ecuador' : 'Apple Authorized Reseller - Store';
      }
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.updateScrollButtons();
    });
  },
  methods: {
    selectCategory(id) {
      if (this.activeCategory === id) {
        this.activeCategory = 'all'; // toggle
      } else {
        this.activeCategory = id;
        this.$nextTick(() => {
          const targetEl = document.getElementById('section-' + id);
          if (targetEl) {
            targetEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        });
      }
    },
    scrollCategories(offset) {
      const el = this.$refs.categoryScrollRef;
      if (el) {
        el.scrollBy({ left: offset, behavior: 'smooth' });
      }
    },
    updateScrollButtons() {
      const el = this.$refs.categoryScrollRef;
      if (!el) return;
      this.canScrollLeft = el.scrollLeft > 10;
      this.canScrollRight = el.scrollLeft < (el.scrollWidth - el.clientWidth - 10);
    },
    scrollTrack(sectionId, offset) {
      const refArray = this.$refs['track_' + sectionId];
      const trackEl = Array.isArray(refArray) ? refArray[0] : refArray;
      if (trackEl) {
        trackEl.scrollBy({ left: offset, behavior: 'smooth' });
      }
    },
    goToForm(product) {
      this.$router.push({
        path: '/form',
        query: product ? { item: product.id } : {}
      });
    }
  }
}
</script>

<style scoped>
.apple-store-page {
  min-height: 100vh;
  background-color: #f5f5f7;
  color: #1d1d1f;
  display: flex;
  flex-direction: column;
}

.store-main {
  flex: 1;
  width: 100%;
  padding-top: 44px; /* Global nav offset */
}

/* Store Top Section */
.store-top-section {
  background: #f5f5f7;
  padding: 8% 8%;
  width: 100%;
  box-sizing: border-box;
}

.store-header-container {
  max-width: 1200px;
  margin: 0 auto;
}

/* Title Split */
.store-title-split {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 56px;
  gap: 32px;
}

.store-main-title {
  font-size: clamp(48px, 8vw, 80px);
  font-weight: 550;
  letter-spacing: -0.025em;
  line-height: 1.05;
  color: #1d1d1f;
  margin: 0;
  font-family: var(--apple-font);
}

.store-title-right {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  max-width: 440px;
}

.store-tagline {
  text-align: right;
  font-size: clamp(22px, 2.5vw, 28px);
  font-weight: 600;
  letter-spacing: -0.02em;
  line-height: 1.15;
  color: #1d1d1f;
  margin: 0 0 16px 0;
}

.store-quick-links {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.store-quick-links a {
  font-size: 14px;
  color: #0066cc;
  text-decoration: none;
  font-weight: 400;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  transition: color 0.15s ease, text-decoration 0.15s ease;
}

.store-quick-links a:hover {
  text-decoration: underline;
  color: #0077ed;
}

.arrow-glyph {
  font-size: 12px;
  display: inline-block;
  transform: translateY(-1px);
}

/* Category Carousel */
.category-carousel-wrapper {
  position: relative;
  width: 100%;
  display: flex;
  align-items: center;
}

.category-carousel {
  display: flex;
  gap: 50px;
  overflow-x: auto;
  scroll-behavior: smooth;
  padding: 10px 4px 18px;
  scrollbar-width: none;
  -ms-overflow-style: none;
  width: 100%;
}

.category-carousel::-webkit-scrollbar {
  display: none;
}

.category-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  min-width: 100px;
  cursor: pointer;
  user-select: none;
  transition: transform 0.2s cubic-bezier(0.16, 1, 0.3, 1);
}

.category-item:hover {
  transform: translateY(-3px);
}

.category-thumb-wrapper {
  height: 110px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 14px;
}

.category-thumb-img {
  max-height: 105px;
  max-width: 140px;
  width: auto;
  height: auto;
  object-fit: contain;
  mix-blend-mode: multiply;
  transition: transform 0.25s ease;
}

.category-item:hover .category-thumb-img {
  transform: scale(1.06);
}

.category-name {
  font-size: 14px;
  font-weight: 600;
  color: #1d1d1f;
  text-align: center;
  white-space: nowrap;
  letter-spacing: -0.01em;
  transition: color 0.2s;
}

.category-item.active .category-name {
  color: #0071e3;
  text-decoration: underline;
  text-underline-offset: 4px;
}

/* Nav Carousel Arrow Buttons */
.carousel-nav-btn {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: rgba(220, 220, 224, 0.85);
  backdrop-filter: blur(12px);
  border: 1px solid rgba(0, 0, 0, 0.08);
  color: #1d1d1f;
  font-size: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;
}

.carousel-nav-btn:hover {
  background: rgba(255, 255, 255, 0.95);
  transform: translateY(-50%) scale(1.08);
}

.prev-btn {
  left: -12px;
}

.next-btn {
  right: -12px;
}

/* Products Section & Category Blocks */
.store-products-section {
  padding: 30px 0 64px;
  background: #f5f5f7;
}

.store-category-block {
  margin-bottom: 56px;
  width: 100%;
}

.category-block-header {
  width: 100%;
  margin: 0 auto 24px;
  padding: 0 8%;
  box-sizing: border-box;
}

.category-block-title {
  font-size: 28px;
  margin: 0;
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 8px;
}

.category-title-bold {
  font-weight: 700;
  color: #1d1d1f;
  font-size: 28px;
  letter-spacing: -0.02em;
}

.category-title-sub {
  font-weight: 500;
  color: #6e6e73;
  font-size: 24px;
  letter-spacing: -0.015em;
}

/* Horizontal Carousel Track */
.carousel-track-wrapper {
  position: relative;
  width: 100%;
}

.products-carousel-track {
  display: flex;
  gap: 24px;
  overflow-x: auto;
  scroll-behavior: smooth;
  scroll-snap-type: x mandatory;
  padding: 14px 8% 32px 8%;
  scrollbar-width: none;
  -ms-overflow-style: none;
  width: 100%;
  box-sizing: border-box;
}

.products-carousel-track::-webkit-scrollbar {
  display: none;
}

.product-card {
  flex: 0 0 400px;
  width: 350px;
  background: #ffffff;
  border-radius: 28px;
  padding: 34px 28px 28px;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;
  box-sizing: border-box;
  cursor: pointer;
}

.product-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 14px 34px rgba(0, 0, 0, 0.08);
}

.card-top-badge {
  position: absolute;
  top: 24px;
  right: 24px;
  background: #34c759;
  color: #ffffff;
  font-size: 11.5px;
  font-weight: 700;
  padding: 3px 9px;
  border-radius: 980px;
  letter-spacing: 0.01em;
  text-transform: uppercase;
  z-index: 2;
  box-shadow: 0 2px 6px rgba(52, 199, 89, 0.25);
}

.card-eyebrow {
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: #b64400;
  margin-bottom: 8px;
}

.card-title {
  font-size: 26px;
  font-weight: 700;
  line-height: 1.15;
  letter-spacing: -0.025em;
  color: #1d1d1f;
  margin: 0 0 20px 0;
  min-height: 60px;
  font-family: var(--apple-font, -apple-system, BlinkMacSystemFont, 'SF Pro Display', sans-serif);
}

/* Image Showcase */
.product-img-wrapper {
  width: 100%;
  height: 220px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 6px 0 20px;
  overflow: hidden;
}

.product-img {
  max-width: 86%;
  max-height: 210px;
  width: auto;
  height: auto;
  object-fit: contain;
  transition: transform 0.4s cubic-bezier(0.16, 1, 0.3, 1);
  user-select: none;
}

.blend-multiply {
  mix-blend-mode: multiply;
}

.product-card:hover .product-img {
  transform: scale(1.04);
}

.img-air-large {
  max-width: 100% !important;
  max-height: 300px !important;
}



/* Color Dots */
.color-dots-row {
  display: flex;
  justify-content: center;
  gap: 10px;
  margin-bottom: 28px;
}

.color-dot {
  width: 13px;
  height: 13px;
  border-radius: 50%;
  box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(0, 0, 0, 0.08);
  display: inline-block;
  cursor: pointer;
  transition: transform 0.2s ease;
}

.color-dot:hover {
  transform: scale(1.25);
}

/* Card Footer */
.card-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-top: auto;
  padding-top: 14px;
}

.card-pricing {
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
}

.price-row {
  display: flex;
  align-items: baseline;
  gap: 6px;
  flex-wrap: wrap;
}

.price-current {
  font-size: 17px;
  line-height: 1.2;
  font-weight: 700;
  color: #1d1d1f;
  letter-spacing: -0.02em;
}

.price-original {
  font-size: 13.5px;
  line-height: 1.2;
  color: #86868b;
  text-decoration: line-through;
  font-weight: 400;
}

.price-badge {
  font-size: 11px;
  font-weight: 600;
  color: #1d883b;
  background: rgba(52, 199, 89, 0.14);
  padding: 1px 6px;
  border-radius: 980px;
  letter-spacing: -0.01em;
}

.price-secondary {
  font-size: 12px;
  line-height: 1.35;
  color: #6e6e73;
  margin: 0;
  letter-spacing: -0.01em;
}

.card-buy-btn {
  background: #0071e3;
  color: #ffffff;
  font-size: 14px;
  font-weight: 400;
  padding: 6px 18px;
  border-radius: 980px;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
  transition: background 0.15s ease, transform 0.15s ease;
  flex-shrink: 0;
}

.card-buy-btn:hover {
  background: #0077ed;
  transform: scale(1.03);
}

/* Track Nav Buttons */
.track-nav-btn {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(225, 225, 230, 0.88);
  backdrop-filter: blur(12px);
  border: 1px solid rgba(0, 0, 0, 0.08);
  color: #1d1d1f;
  font-size: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.12);
  transition: all 0.2s ease;
  z-index: 10;
}

.track-nav-btn:hover {
  background: rgba(255, 255, 255, 0.98);
  transform: translateY(-50%) scale(1.08);
}

.prev-track-btn {
  left: 2%;
}

.next-track-btn {
  right: 2%;
}

/* Perks Section */
.store-perks-section {
  background: #f5f5f7;
  padding: 56px 24px;
  border-top: 1px solid #e5e5ea;
}

.store-perks-container {
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 32px;
}

.perk-item {
  text-align: center;
  padding: 16px;
}

.perk-icon {
  font-size: 36px;
  margin-bottom: 12px;
}

.perk-title {
  font-size: 16px;
  font-weight: 600;
  color: #1d1d1f;
  margin: 0 0 8px 0;
}

.perk-desc {
  font-size: 14px;
  color: #6e6e73;
  line-height: 1.4;
  margin: 0;
}

@media (max-width: 860px) {
  .store-title-split {
    flex-direction: column;
    align-items: flex-start;
    margin-bottom: 36px;
    gap: 18px;
  }

  .store-title-right {
    align-items: flex-start;
    text-align: left;
    max-width: 100%;
  }

  .store-main-title {
    font-size: 44px;
    text-align: left;
  }

  .store-tagline {
    font-size: 22px;
    text-align: left;
  }

  .store-quick-links {
    align-items: flex-start;
  }

  .category-block-title {
    font-size: 22px;
  }

  .category-title-bold {
    font-size: 22px;
  }

  .category-title-sub {
    font-size: 20px;
  }
}

@media (max-width: 768px) {
  .store-top-section {
    padding: 32px 16px 20px;
  }

  .category-carousel {
    gap: 28px;
    padding: 10px 8px 22px;
  }

  .category-item {
    min-width: 135px;
  }

  .category-thumb-wrapper {
    height: 120px;
    margin-bottom: 12px;
  }

  .category-thumb-img {
    max-height: 116px;
    max-width: 155px;
  }

  .category-name {
    font-size: 14px;
    font-weight: 600;
  }

  .carousel-nav-btn {
    display: none;
  }

  .store-category-block {
    margin-bottom: 40px;
  }

  .category-block-header {
    padding: 0 16px;
    margin-bottom: 12px;
  }

  .products-carousel-track {
    padding: 8px 16px 20px;
    gap: 16px;
  }

  .product-card {
    flex: 0 0 310px;
    width: 310px;
    padding: 26px 20px 22px;
    border-radius: 22px;
  }

  .card-title {
    font-size: 22px;
    min-height: 52px;
    margin-bottom: 14px;
  }

  .product-img-wrapper {
    height: 180px;
    margin: 4px 0 16px;
  }

  .product-img {
    max-height: 170px;
  }

  .color-dots-row {
    margin-bottom: 20px;
  }

  .track-nav-btn {
    display: none;
  }
}
</style>
